import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import useEntityStore from "../../stores/entityStore";
import { resolveEntityIds, resolveEntityId } from "./entityIdResolver";

const log = createFileLogger('ENTITIES_GROUP');

export const entitiesGroupCommand: SelfContainedCommand = {
  id: 'entities_group',
  description: `Create a new folder and move multiple entities into it in one operation.

**Use Cases:**
- Group related documents together
- Organize multiple selected files
- Create project-specific folders
- Clean up workspace by grouping items`,
  autonomyLevel: 'ask',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'entityIds',
        type: 'array',
        description: 'Array of entity IDs to group together',
        required: true
      },
      {
        name: 'folderName',
        type: 'string',
        description: 'Name for the new folder',
        required: true
      },
      {
        name: 'parentFolderId',
        type: 'string',
        description: 'Optional parent folder to create the new folder inside',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: {
          entityIds: ['doc_1', 'doc_2', 'doc_3'],
          folderName: 'Research Papers'
        },
        expectedOutput: createSuccessResult('Entities grouped successfully', {
          folderId: 'group_123',
          entityIds: ['doc_1', 'doc_2', 'doc_3']
        }),
        description: 'Group multiple documents into a new folder',
        context: 'When organizing related documents'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 800,
    complexity: 'high'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    let { entityIds, folderName, parentFolderId } = params;
    
    try {
      // ✅ Resolve llmIds to _ids if needed
      const resolution = resolveEntityIds(entityIds);
      
      if (resolution.failed.length > 0) {
        return createErrorResult(
          'Some entities not found',
          `Could not find entities with llmIds: ${resolution.failed.join(', ')}`,
          'validation',
          { data: { failedIds: resolution.failed } }
        );
      }
      
      entityIds = resolution.resolved;
      
      // Also resolve parentFolderId if provided
      if (parentFolderId) {
        const parentResolution = resolveEntityId(parentFolderId);
        if (parentResolution.isLlmId && !parentResolution.entity) {
          return createErrorResult(
            'Parent folder not found',
            `No folder found with llmId: ${parentFolderId}`,
            'validation',
            { data: { providedId: parentFolderId } }
          );
        }
        parentFolderId = parentResolution.resolved;
      }
      
      log.info('[entities_group] Grouping entities:', { 
        originalIds: params.entityIds,
        resolvedIds: entityIds,
        folderName, 
        parentFolderId 
      });
      
      const entityStore = useEntityStore.getState();
      const result = await entityStore.actions.groupEntities(
        entityIds,
        folderName,
        parentFolderId
      );
      
      log.info('[entities_group] ✅ Entities grouped:', { folderId: result.folderId });
      
      return createSuccessResult(
        `Grouped ${entityIds.length} entities into folder "${folderName}"`,
        result,
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'entityStore.actions',
            affectedEntities: [result.folderId, ...entityIds]
          } as any,
          ui: {
            refreshContext: true
          }
        }
      );
      
    } catch (error: any) {
      log.error('[entities_group] Error:', error);
      return createErrorResult(
        'Failed to group entities',
        error.message || 'Unknown error',
        'execution',
        {
          data: { entityIds, folderName },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

